import { Component, Input, OnInit } from '@angular/core';
import { Role } from 'app/shared/models/role';
import { FormBuilder, FormControl, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { DialogResult, NullEntity, languageMap, EntityDetailsGeneralBaseComponent } from '@kpd/framework-ngx';
import { ActivatedRoute } from '@angular/router';
import { RolesNavigatorHelper } from 'app/roles/roles-navigator-helper';
import { ApplicationService } from 'app/shared/services/application.service';
import { Application } from 'app/shared/models/application';
import { HttpErrorResponse } from '@angular/common/http';
import { Permissions } from 'app/shared/services/permissions';
import { UMLocalizationService } from 'app/shared/services/localization.service';

@Component({
  selector: 'app-role-details-general',
  templateUrl: './role-details-general.component.html',
})
export class RoleDetailsGeneralComponent extends EntityDetailsGeneralBaseComponent implements OnInit {
  Permissions: typeof Permissions = Permissions;
  @Input()
  item: Role;
  applications: Application[] = [];
  languageFields: AbstractControl[];
  descriptionsFormGroup: FormGroup = new FormGroup({});
  userLanguage = 'dutch';

  applicationColumns = [
    { name: 'name', title: 'Name', width: '50%' },
    { name: 'description', title: 'Description', width: '50%' },
  ];

  constructor(
    private formBuilder: FormBuilder,
    protected navigatorHelper: RolesNavigatorHelper,
    private applicationService: ApplicationService,
    localizationService: UMLocalizationService,
    activatedRoute: ActivatedRoute,
  ) {
    super(localizationService as any, activatedRoute);
    this.userLanguage = this.getLanguage();
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  getLanguage(): string {
    const langCode = this.localizationService.getLanguage();
    if (langCode) {
      return languageMap[langCode];
    }
    return 'english';
  }

  createForm(): FormGroup {
    this.descriptionsFormGroup = new FormGroup({
      dutch: new FormControl(this.item.description ? this.item.description.dutch : undefined, [
        Validators.maxLength(500),
      ]),
      english: new FormControl(this.item.description ? this.item.description.english : undefined, [
        Validators.maxLength(500),
      ]),
      french: new FormControl(this.item.description ? this.item.description.french : undefined, [
        Validators.maxLength(500),
      ]),
      german: new FormControl(this.item.description ? this.item.description.german : undefined, [
        Validators.maxLength(500),
      ]),
    });

    const form = this.formBuilder.group({
      name: new FormControl(this.item.name, [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(/^(?!.*\/).*$/), // No forward slash allowed
      ]),
      application: new FormControl(
        this.item.application ? { name: this.item.application } : undefined,
        Validators.required,
      ),
      description: new FormControl(
        this.item.description ? this.item.description[this.userLanguage] : undefined,
        Validators.maxLength(500),
      ),
      isAssignedByDefault: new FormControl(this.item.isAssignedByDefault || false),
      descriptions: this.descriptionsFormGroup,
    });

    if (this.item.isNew()) {
      this.applicationService.getApplicationsByTenant().subscribe((apps) => {
        this.applications = apps;
        const app = this.applications.find((application) => application.name === this.item.application);
        form.patchValue({
          application: app,
        });
      });
    }

    this.languageFields = Object.values(this.descriptionsFormGroup.controls);

    return form;
  }

  formToEntity(): Role {
    const role: Role = this.item.clone();
    role.name = this.form.value.name;
    role.application = this.form.value.application ? this.form.value.application.name : '';
    role.isAssignedByDefault = this.form.value.isAssignedByDefault;
    role.description.dutch = this.descriptionsFormGroup.value.dutch;
    role.description.english = this.descriptionsFormGroup.value.english;
    role.description.french = this.descriptionsFormGroup.value.french;
    role.description.german = this.descriptionsFormGroup.value.german;
    return role;
  }

  executeSave(form: FormGroup): void {
    const isNameChanged = (form.get('name') as AbstractControl).dirty || this.item.isNew();
    this.updater.saveEntity(this.formToEntity(), isNameChanged).subscribe(
      () => {
        this.form.markAsPristine();
      },
      (err: HttpErrorResponse) => {
        if (err && err.status === 409) {
          (form.get('name') as any).setErrors({ roleNameConflict: true });
          this.setFieldErrors(true);
        }
      },
    );
  }

  isValid(form: FormGroup): boolean {
    if (!form.dirty) {
      return true;
    }

    if (form.invalid) {
      this.setFieldErrors(true);
      return false;
    }

    return true;
  }

  onRevert(): void {
    if (!this.form.dirty) {
      return;
    }
    this.updater.confirmAndRevert(this.item).subscribe((result: DialogResult) => {
      if (result === DialogResult.Verify) {
        this.form = this.createForm();
        this.setFieldErrors(false);
      }
    });
  }

  onAdd(): void {
    if (this.form.invalid) {
      this.setFieldErrors(true);
    }

    this.navigateToCreateRole();
  }

  navigateToCreateRole(): void {
    this.navigatorHelper.navigateToCreateFromDetail(false);
  }

  hasError(fieldName: string): boolean {
    if (this.item instanceof NullEntity || !this.item) {
      return false;
    }
    return super.hasError(fieldName);
  }

  hasTranslationSync(translation: string): AbstractControl | null {
    return this.userLanguage === translation ? this.form.controls.description : null;
  }
}
